// src/services/estoque/ImportacaoXmlService.js
const CODIGOS_CST_COM_ST = ['10', '30', '60', '70'];
const CODIGOS_CSOSN_COM_ST = ['201', '202', '203', '500'];
const CODIGOS_PIS_COFINS_ESPECIFICOS_SAIDA = ['04', '05', '06', '07', '08', '09'];

const normalizarCodigo = (codigo) => String(codigo || '').trim();

const obterTexto = (elemento, seletor) => (
  elemento?.querySelector(seletor)?.textContent?.trim() || ''
);

export const converterCfopFornecedorParaEntrada = (cfopFornecedor) => {
  const cfop = normalizarCodigo(cfopFornecedor);

  if (!/^\d{4}$/.test(cfop)) return '1102';

  const prefixosEntrada = {
    5: '1',
    6: '2',
    7: '3',
  };

  const prefixoEntrada = prefixosEntrada[cfop[0]];
  return prefixoEntrada ? `${prefixoEntrada}${cfop.slice(1)}` : cfop;
};

const possuiSubstituicaoTributaria = ({
  cfopFornecedor,
  cstIcms,
  csosnFornecedor,
  valorIcmsSt,
}) => {
  const cfop = normalizarCodigo(cfopFornecedor);
  const cst = normalizarCodigo(cstIcms).padStart(2, '0');
  const csosn = normalizarCodigo(csosnFornecedor);
  const cfopVendaComSt = /^[56]40[1-5]$/.test(cfop);
  const possuiValorIcmsSt = Number(String(valorIcmsSt || '0').replace(',', '.')) > 0;
  const outroCodigoComSt = possuiValorIcmsSt && (cst === '90' || csosn === '900');

  return cfopVendaComSt
    || CODIGOS_CST_COM_ST.includes(cst)
    || CODIGOS_CSOSN_COM_ST.includes(csosn)
    || outroCodigoComSt;
};

export const montarTributacaoVendaSimplesNacional = ({
  cfopFornecedor,
  cstIcms,
  csosnFornecedor,
  valorIcmsSt,
  cstPis,
  cstCofins,
}) => {
  const mercadoriaComSt = possuiSubstituicaoTributaria({
    cfopFornecedor,
    cstIcms,
    csosnFornecedor,
    valorIcmsSt,
  });
  const cstPisEntrada = normalizarCodigo(cstPis);
  const cstCofinsEntrada = normalizarCodigo(cstCofins);
  const cstEntradaComum = cstPisEntrada && cstPisEntrada === cstCofinsEntrada
    ? cstPisEntrada
    : '99';
  const cstSaida = CODIGOS_PIS_COFINS_ESPECIFICOS_SAIDA.includes(cstEntradaComum)
    ? cstEntradaComum
    : '49';

  return {
    cfop: mercadoriaComSt ? '5405' : '5102',
    csosn: mercadoriaComSt ? '500' : '102',
    cstPisCofinsEnt: cstEntradaComum,
    cstPisCofinsSai: cstSaida,
  };
};

export const extrairTributacaoItemXml = (det) => {
  const cfopFornecedor = obterTexto(det, 'prod > CFOP');
  const grupoIcms = det?.querySelector('imposto > ICMS')?.firstElementChild;
  const cstIcms = obterTexto(grupoIcms, 'CST');
  const csosnFornecedor = obterTexto(grupoIcms, 'CSOSN');
  const valorIcmsSt = obterTexto(grupoIcms, 'vICMSST');
  const cstPis = obterTexto(det, 'imposto > PIS CST');
  const cstCofins = obterTexto(det, 'imposto > COFINS CST');

  return {
    cfopFornecedor,
    cfopEntrada: converterCfopFornecedorParaEntrada(cfopFornecedor),
    cstIcms,
    csosnFornecedor,
    valorIcmsSt,
    cstPis,
    cstCofins,
    tributacaoVenda: montarTributacaoVendaSimplesNacional({
      cfopFornecedor,
      cstIcms,
      csosnFornecedor,
      valorIcmsSt,
      cstPis,
      cstCofins,
    }),
  };
};

const obterNumero = (elemento, seletor) => {
  const valor = obterTexto(elemento, seletor).replace(',', '.');
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

const obterDataEmissao = (xmlDoc) => {
  const valor = obterTexto(xmlDoc, 'ide > dhEmi') || obterTexto(xmlDoc, 'ide > dEmi');
  return valor ? valor.slice(0, 10) : '';
};

export const parseNFeXml = (xmlText) => {
  if (!xmlText?.trim()) {
    throw new Error('O arquivo XML está vazio.');
  }

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
  if (xmlDoc.querySelector('parsererror')) {
    throw new Error('O arquivo selecionado não contém um XML válido.');
  }

  const infNFe = xmlDoc.querySelector('infNFe');
  if (!infNFe) {
    throw new Error('O XML não possui a estrutura de uma NF-e.');
  }

  const cnpj = obterTexto(xmlDoc, 'emit > CNPJ');
  const cpf = obterTexto(xmlDoc, 'emit > CPF');
  if (!cnpj && !cpf) {
    throw new Error('Não foi possível identificar o CNPJ ou CPF do fornecedor no XML.');
  }

  const itensNodes = Array.from(xmlDoc.querySelectorAll('det'));
  if (itensNodes.length === 0) {
    throw new Error('A NF-e não possui itens para importar.');
  }

  const total = xmlDoc.querySelector('total > ICMSTot');
  const totaisOriginais = {
    vProd: obterNumero(total, 'vProd'),
    vDesc: obterNumero(total, 'vDesc'),
    vFrete: obterNumero(total, 'vFrete'),
    vOutro: obterNumero(total, 'vOutro'),
    vNota: obterNumero(total, 'vNF'),
  };

  const itens = itensNodes.map((det, index) => {
    const quantidade = obterNumero(det, 'prod > qCom');
    const valorUnit = obterNumero(det, 'prod > vUnCom');
    const valorTotal = obterNumero(det, 'prod > vProd');

    return {
      item: Number(det.getAttribute('nItem')) || index + 1,
      cProd: obterTexto(det, 'prod > cProd'),
      ean: obterTexto(det, 'prod > cEAN'),
      descricao: obterTexto(det, 'prod > xProd'),
      ncm: obterTexto(det, 'prod > NCM'),
      cest: obterTexto(det, 'prod > CEST'),
      unidade: obterTexto(det, 'prod > uCom') || 'UN',
      quantidade,
      valorUnit,
      valorTotal,
      quantidadeOriginal: quantidade,
      valorUnitOriginal: valorUnit,
      valorTotalOriginal: valorTotal,
      ...extrairTributacaoItemXml(det),
      statusVinculo: 'pendente',
      produtoId: null,
      produtoDescricao: '',
      candidatos: [],
    };
  });

  return {
    nota: {
      numero: obterTexto(xmlDoc, 'ide > nNF'),
      serie: obterTexto(xmlDoc, 'ide > serie'),
      modeloNF: obterTexto(xmlDoc, 'ide > mod'),
      dataEmissao: obterDataEmissao(xmlDoc),
      chave: infNFe.getAttribute('Id')?.replace(/^NFe/, '').trim() || '',
      naturezaOperacao: obterTexto(xmlDoc, 'ide > natOp'),
      fornecedorNome: obterTexto(xmlDoc, 'emit > xNome'),
      fornecedorFantasia: obterTexto(xmlDoc, 'emit > xFant'),
      cnpj,
      cpf,
      ie: obterTexto(xmlDoc, 'emit > IE'),
      totaisOriginais,
      ...totaisOriginais,
    },
    fornecedor: {
      cnpj,
      cpf,
      nome: obterTexto(xmlDoc, 'emit > xNome'),
      fantasia: obterTexto(xmlDoc, 'emit > xFant'),
      ie: obterTexto(xmlDoc, 'emit > IE'),
      email: obterTexto(xmlDoc, 'emit > email'),
      telefone: obterTexto(xmlDoc, 'emit > enderEmit > fone'),
      logradouro: obterTexto(xmlDoc, 'emit > enderEmit > xLgr'),
      numero: obterTexto(xmlDoc, 'emit > enderEmit > nro'),
      complemento: obterTexto(xmlDoc, 'emit > enderEmit > xCpl'),
      bairro: obterTexto(xmlDoc, 'emit > enderEmit > xBairro'),
      codigoMunicipio: obterTexto(xmlDoc, 'emit > enderEmit > cMun'),
      cep: obterTexto(xmlDoc, 'emit > enderEmit > CEP'),
    },
    itens,
  };
};

export const calcularTotaisImportacao = (nota, itens) => {
  const numero = (valor) => {
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
    const texto = String(valor ?? '').trim();
    if (!texto) return 0;
    const normalizado = texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto;
    const resultado = Number(normalizado);
    return Number.isFinite(resultado) ? resultado : 0;
  };
  const originais = nota?.totaisOriginais || {};
  const vProd = itens.reduce((total, item) => total + numero(item.valorTotal), 0);
  const vDesc = numero(nota?.vDesc);
  const vFrete = numero(nota?.vFrete);
  const vOutro = numero(nota?.vOutro);
  const vNotaOriginal = numero(originais.vNota);

  const vNota = vNotaOriginal
    + (vProd - numero(originais.vProd))
    - (vDesc - numero(originais.vDesc))
    + (vFrete - numero(originais.vFrete))
    + (vOutro - numero(originais.vOutro));

  return { vProd, vDesc, vFrete, vOutro, vNota };
};

export const importacaoPossuiDivergencia = (nota, itens) => {
  const margem = 0.0001;
  const totais = calcularTotaisImportacao(nota, itens);
  const originais = nota?.totaisOriginais || {};

  return Math.abs(totais.vProd - Number(originais.vProd || 0)) > margem
    || Math.abs(totais.vDesc - Number(originais.vDesc || 0)) > margem
    || Math.abs(totais.vFrete - Number(originais.vFrete || 0)) > margem
    || Math.abs(totais.vOutro - Number(originais.vOutro || 0)) > margem;
};
